import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Loader2, Droplet, CircleDot } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { VehiclePicker } from '@/components/common/VehiclePicker';
import { apiClient, extractErrorMessage } from '@/lib/api';

/**
 * Oil drums and tyres — the two consumables that do not behave like parts.
 *
 * A drum is a volume, so it is drawn as one: the gauge is the number. Reading
 * "193" tells you less in a glance than seeing a drum most of the way full, and
 * a mechanic deciding whether to open a new one is glancing, not reading.
 *
 * A tyre is a serialised object with a location, so it is a row: which tyre,
 * which truck, which corner.
 */

export interface Drum {
  id: string; label: string; grade: string;
  capacity_liters: string; remaining_liters: string;
  purchase_price_sar: string; cost_per_liter_sar: string;
  purchased_at: string; is_active: boolean; draw_count: number;
}
export interface Tire {
  id: string; serial_number: string; brand: string; size_model: string | null;
  unit_cost_sar: string | null; status: string; position: string | null;
  vehicle_plate: string | null; vehicle_code: string | null;
}

export const TIRE_BRANDS = ['Michelin', 'Bridgestone', 'Goodyear', 'Dunlop',
  'Yokohama', 'Hankook', 'Continental', 'Pirelli'];

const sar = (v: unknown) =>
  (v == null ? '—' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** A number that moves to its new value rather than jumping. */
function useCountUp(target: number, ms = 600) {
  const [shown, setShown] = useState(target);
  const from = useRef(target);
  useEffect(() => {
    // Respect a reduced-motion preference: land on the value at once.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(target); from.current = target; return; }
    const start = performance.now();
    const a = from.current;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(a + (target - a) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return shown;
}

/** One drum, drawn to scale. */
function DrumCard({ d, onRemove }: { d: Drum; onRemove: (d: Drum) => void }) {
  const cap = Number(d.capacity_liters);
  const left = Number(d.remaining_liters);
  const pct = cap > 0 ? Math.max(0, Math.min(100, (left / cap) * 100)) : 0;
  const shownLiters = useCountUp(left);
  const low = pct <= 15;
  const empty = left <= 0;

  // Body of the drum in SVG units; the liquid fills from the bottom up.
  const TOP = 26, BOT = 150, H = BOT - TOP;
  const liquidTop = BOT - (H * pct) / 100;

  return (
    <div className={`rounded-xl border p-3 flex gap-3 transition-colors ${
      empty ? 'border-border opacity-70'
        : low ? 'border-rose-500/60 bg-rose-500/5'
        : 'border-border'}`}>
      <svg viewBox="0 0 84 164" className="w-16 h-auto shrink-0" role="img"
        aria-label={`${d.label}: ${left} of ${cap} litres remaining`}>
        <defs>
          <clipPath id={`clip-${d.id}`}>
            <rect x="12" y={TOP} width="60" height={H} rx="6" />
          </clipPath>
        </defs>
        {/* the drum */}
        <rect x="12" y={TOP} width="60" height={H} rx="6" className="fill-muted stroke-border" strokeWidth="2" />
        {/* the oil, animated by height */}
        <g clipPath={`url(#clip-${d.id})`}>
          <rect x="12" width="60" y={liquidTop} height={BOT - liquidTop}
            className={low ? 'fill-rose-500/70' : 'fill-amber-500/70'}
            style={{ transition: 'y 700ms cubic-bezier(.22,1,.36,1), height 700ms cubic-bezier(.22,1,.36,1)' }} />
          {/* a surface line, so a full drum still reads as liquid */}
          {pct > 0 && (
            <rect x="12" width="60" y={liquidTop} height="2.5"
              className={low ? 'fill-rose-400' : 'fill-amber-300'}
              style={{ transition: 'y 700ms cubic-bezier(.22,1,.36,1)' }} />
          )}
        </g>
        {/* hoops and lid, so it reads as a drum and not a beaker */}
        <rect x="12" y={TOP + H * 0.3} width="60" height="2" className="fill-border" />
        <rect x="12" y={TOP + H * 0.65} width="60" height="2" className="fill-border" />
        <rect x="20" y="14" width="44" height="12" rx="3" className="fill-muted stroke-border" strokeWidth="2" />
      </svg>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium text-sm truncate">{d.label}</div>
          <button type="button" onClick={() => onRemove(d)}
            className="text-[11px] text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400 shrink-0"
            aria-label={`Remove ${d.label}`}>Delete</button>
        </div>
        <div className="text-xs text-muted-foreground">{d.grade} · bought {String(d.purchased_at).slice(0, 10)}</div>
        <div className="mt-1.5 flex items-baseline gap-1">
          <span className={`text-xl font-mono ${low ? 'text-rose-600 dark:text-rose-400' : ''}`}>
            {shownLiters.toFixed(0)}
          </span>
          <span className="text-xs text-muted-foreground">/ {cap.toFixed(0)} L</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {sar(d.cost_per_liter_sar)} SAR/L · {sar(d.purchase_price_sar)} SAR total
        </div>
        {empty ? (
          <div className="text-[11px] text-muted-foreground mt-1">Empty</div>
        ) : low ? (
          <div className="text-[11px] text-rose-600 dark:text-rose-400 mt-1 font-medium">
            Low — {pct.toFixed(0)}% left
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function OilDrumsPanel() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [removingDrum, setRemovingDrum] = useState<Drum | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const removeDrum = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete<{ retired?: boolean; message?: string }>(
        `/api/v1/consumables/oil-drums/${id}`)).data,
    onSuccess: (r) => {
      setRemovingDrum(null);
      setNote(r.retired ? (r.message ?? 'Retired.') : null);
      void qc.invalidateQueries({ queryKey: ['oil-drums'] });
    },
  });
  const { data, isLoading } = useQuery({
    queryKey: ['oil-drums'],
    queryFn: async () => (await apiClient.get<{ items: Drum[] }>('/api/v1/consumables/oil-drums')).data.items,
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {isLoading ? 'loading…' : `${data?.length ?? 0} drum(s)`}
        </span>
        <Button size="sm" className="gap-2" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" /> Add oil drum
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (data ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <Droplet className="h-8 w-8 mx-auto text-muted-foreground" />
          <div className="mt-3 font-medium">No oil drums yet</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Add a drum with what it cost and how much it holds. Its price per litre is worked out
            from those two, so every oil change charges the truck the real cost of what it drank.
          </p>
          <Button size="sm" className="mt-4 gap-2" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add the first drum
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(data ?? []).map((d) => <DrumCard key={d.id} d={d} onRemove={setRemovingDrum} />)}
        </div>
      )}

      {note && <p className="text-xs text-muted-foreground">{note}</p>}

      {/* Asked by name, because a drum card is a small target. */}
      {removingDrum && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
             role="dialog" aria-modal="true" onClick={() => setRemovingDrum(null)}>
          <div className="bg-card border border-border rounded-xl p-4 w-full max-w-sm space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <div className="font-medium">Delete “{removingDrum.label}”?</div>
            <p className="text-sm text-muted-foreground">
              If oil from it has been charged to a job it is retired instead, so those jobs keep their cost.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setRemovingDrum(null)}>Cancel</Button>
              <Button variant="gold" className="flex-1" disabled={removeDrum.isPending}
                onClick={() => removeDrum.mutate(removingDrum.id)}>
                {removeDrum.isPending ? 'Working…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {addOpen && <AddDrumSheet onClose={() => setAddOpen(false)} onSaved={() => {
        setAddOpen(false);
        void qc.invalidateQueries({ queryKey: ['oil-drums'] });
      }} />}
    </div>
  );
}

function AddDrumSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [label, setLabel] = useState('');
  const [grade, setGrade] = useState('15W40');
  const [cap, setCap] = useState('208');
  const [price, setPrice] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!label.trim()) throw new Error('Give the drum a label, e.g. “Drum #01”.');
      if (!(Number(cap) > 0)) throw new Error('How many litres does it hold?');
      return apiClient.post('/api/v1/consumables/oil-drums', {
        label: label.trim(), grade: grade.trim() || 'unspecified',
        capacityLiters: Number(cap), purchasePriceSar: Number(price) || 0,
      });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(extractErrorMessage(e)),
  });

  const perLiter = Number(cap) > 0 && Number(price) > 0 ? Number(price) / Number(cap) : null;

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle>Add oil drum</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Drum label *</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Drum #01" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Grade</label>
            <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="15W40" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Capacity (litres) *</label>
              <Input inputMode="decimal" value={cap} onChange={(e) => setCap(e.target.value)} placeholder="208" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Purchase price SAR</label>
              <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
            </div>
          </div>
          {/* Shown before saving, because this is the number every oil change
              will be charged at and a typo here is a typo on every job after. */}
          {perLiter !== null && (
            <div className="rounded-lg border border-border px-3 py-2 text-sm">
              Cost per litre: <b className="font-mono">{perLiter.toFixed(4)} SAR</b>
            </div>
          )}
          {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button variant="gold" className="flex-1" disabled={save.isPending}
              onClick={() => { setErr(null); save.mutate(); }}>
              {save.isPending ? 'Saving…' : 'Add drum'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function TiresPanel() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [mounting, setMounting] = useState<Tire | null>(null);
  const [tyreNote, setTyreNote] = useState<string | null>(null);

  const after = () => { void qc.invalidateQueries({ queryKey: ['tires'] }); };
  const removeTire = useMutation({
    mutationFn: async (id: string) => apiClient.delete(`/api/v1/consumables/tires/${id}`),
    onSuccess: () => { setTyreNote(null); after(); },
    onError: (e) => setTyreNote(extractErrorMessage(e)),
  });
  const unmount = useMutation({
    mutationFn: async (id: string) => apiClient.post(`/api/v1/consumables/tires/${id}/unmount`, {}),
    onSuccess: () => { setTyreNote(null); after(); },
    onError: (e) => setTyreNote(extractErrorMessage(e)),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['tires'],
    queryFn: async () => (await apiClient.get<{ items: Tire[] }>('/api/v1/consumables/tires')).data.items,
  });

  const inStock = (data ?? []).filter((t) => t.status === 'in_stock').length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm text-muted-foreground">
          {isLoading ? 'loading…' : `${data?.length ?? 0} tyre(s) · ${inStock} in stock`}
        </span>
        <Button size="sm" className="gap-2" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4" /> Add tyre
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (data ?? []).length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <CircleDot className="h-8 w-8 mx-auto text-muted-foreground" />
          <div className="mt-3 font-medium">No tyres recorded</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            Each tyre is tracked by the serial number moulded into it, so you can always answer
            which truck it went on and what it cost.
          </p>
          <Button size="sm" className="mt-4 gap-2" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" /> Add the first tyre
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs sticky top-0">
              <tr>
                <th className="text-start px-3 py-2 font-medium">Serial</th>
                <th className="text-start px-3 py-2 font-medium">Brand</th>
                <th className="text-start px-3 py-2 font-medium">Size / model</th>
                <th className="text-end px-3 py-2 font-medium">Cost SAR</th>
                <th className="text-start px-3 py-2 font-medium">Status</th>
                <th className="text-start px-3 py-2 font-medium">Vehicle / position</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((t) => (
                <tr key={t.id} className="border-t border-border hover:bg-muted/20 transition-colors">
                  <td className="px-3 py-2 font-mono text-xs">{t.serial_number}</td>
                  <td className="px-3 py-2">{t.brand}</td>
                  <td className="px-3 py-2 text-muted-foreground">{t.size_model ?? '—'}</td>
                  <td className="px-3 py-2 text-end font-mono">{sar(t.unit_cost_sar)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] rounded-full border px-2 py-0.5 capitalize transition-colors ${
                      t.status === 'mounted'
                        ? 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400'
                        : t.status === 'scrapped'
                          ? 'border-border text-muted-foreground'
                          : 'border-amber-500/50 text-amber-600 dark:text-amber-400'}`}>
                      {t.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {t.vehicle_plate
                      ? <span className="font-mono text-xs">{t.vehicle_plate}{t.position ? ` · ${t.position}` : ''}</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-end whitespace-nowrap">
                    {t.status === 'in_stock' ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setMounting(t)}>Mount</Button>
                        <button type="button" onClick={() => removeTire.mutate(t.id)}
                          className="ms-2 text-xs text-muted-foreground hover:text-rose-600 dark:hover:text-rose-400">
                          Delete
                        </button>
                      </>
                    ) : t.status === 'mounted' ? (
                      // A tyre on a truck comes off before its record goes.
                      <button type="button" onClick={() => unmount.mutate(t.id)}
                        className="text-xs text-muted-foreground hover:text-foreground underline">
                        Take off the truck
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tyreNote && <p className="text-xs text-rose-600 dark:text-rose-400">{tyreNote}</p>}

      {addOpen && <AddTireSheet onClose={() => setAddOpen(false)} onSaved={() => {
        setAddOpen(false); void qc.invalidateQueries({ queryKey: ['tires'] });
      }} />}
      {mounting && <MountTireSheet tire={mounting} onClose={() => setMounting(null)} onSaved={() => {
        setMounting(null); void qc.invalidateQueries({ queryKey: ['tires'] });
      }} />}
    </div>
  );
}

function AddTireSheet({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [serial, setSerial] = useState('');
  const [brand, setBrand] = useState(TIRE_BRANDS[0]);
  const [custom, setCustom] = useState('');
  const [size, setSize] = useState('');
  const [cost, setCost] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!serial.trim()) throw new Error('A tyre is identified by its serial number.');
      return apiClient.post('/api/v1/consumables/tires', {
        serialNumber: serial.trim(),
        brand: brand === 'Custom' ? (custom.trim() || 'Custom') : brand,
        ...(size.trim() && { sizeModel: size.trim() }),
        ...(Number(cost) > 0 && { unitCostSar: Number(cost) }),
      });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(extractErrorMessage(e)),
  });

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle>Add tyre</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Serial number *</label>
            <Input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="As moulded on the tyre" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Brand</label>
            <select value={brand} onChange={(e) => setBrand(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm">
              {[...TIRE_BRANDS, 'Custom'].map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          {brand === 'Custom' && (
            <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Brand name" />
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Size / model</label>
              <Input value={size} onChange={(e) => setSize(e.target.value)} placeholder="315/80R22.5" />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Unit cost SAR</label>
              <Input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
            </div>
          </div>
          {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button variant="gold" className="flex-1" disabled={save.isPending}
              onClick={() => { setErr(null); save.mutate(); }}>
              {save.isPending ? 'Saving…' : 'Add tyre'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MountTireSheet({ tire, onClose, onSaved }: {
  tire: Tire; onClose: () => void; onSaved: () => void;
}) {
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  const [position, setPosition] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      if (!vehicleId) throw new Error('Which truck is it going on?');
      return apiClient.post(`/api/v1/consumables/tires/${tire.id}/mount`, {
        vehicleId, ...(position.trim() && { position: position.trim() }),
      });
    },
    onSuccess: onSaved,
    onError: (e) => setErr(extractErrorMessage(e)),
  });

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md overflow-y-auto">
        <SheetHeader><SheetTitle>Mount {tire.brand} · {tire.serial_number}</SheetTitle></SheetHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Truck *</label>
            <VehiclePicker value={vehicleId} onChange={setVehicleId} placeholder="Search a plate…" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Axle position</label>
            <Input value={position} onChange={(e) => setPosition(e.target.value)}
              placeholder="e.g. rear-left-1" />
            <p className="text-[11px] text-muted-foreground">
              Which corner it went on, so a worn tyre can be traced to a position.
            </p>
          </div>
          {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button variant="gold" className="flex-1" disabled={save.isPending}
              onClick={() => { setErr(null); save.mutate(); }}>
              {save.isPending ? 'Mounting…' : 'Mount to truck'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
